import { TIMEZONE_OPTIONS, validateTimezone } from "@compass/shared/schema/timezone";
import { type TipTapDoc, emptyTipTapDoc } from "@compass/shared/schema/tiptapDoc";
import * as Sentry from "@sentry/react";
import { useForm } from "@tanstack/react-form";
import { Globe, Star } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as BioIcon } from "../../../assets/img/user-profile/bio.svg";
import { ReactComponent as EmailIcon } from "../../../assets/img/user-profile/email.svg";
import { ReactComponent as OrganizationIcon } from "../../../assets/img/user-profile/organization.svg";
import { ReactComponent as ProfileIcon } from "../../../assets/img/user-profile/profile.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { Button, IconWithTooltip, Input, TextArea, ValidationMessage } from "components/elements";
import { AskMeAbout } from "components/form/userProfile/AskMeAbout";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { useStore } from "@tanstack/react-store";
import { ProfileUpdate, ProfileUpdateSchema } from "@compass/shared/types";

export const ProfileForm: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const currentUser = useCurrentUser();
    const { enqueueSnackbar } = useSnackbar();
    const utils = trpc.useUtils();

    // useCurrentUser is query-backed — first render returns undefined, then
    // the user data arrives. Seed the editor doc with whatever's available
    // on first render, then sync once when the user loads (but not after the
    // editor has been touched, so we don't clobber unsaved edits).
    const [aboutMeContentJson, setAboutMeContentJson] = useState<TipTapDoc>(
        () => (currentUser?.aboutMeContentJson as TipTapDoc | undefined) ?? emptyTipTapDoc(),
    );
    const editorTouchedRef = useRef(false);
    useEffect(() => {
        if (editorTouchedRef.current) return;
        const doc = currentUser?.aboutMeContentJson as TipTapDoc | undefined;
        if (doc) setAboutMeContentJson(doc);
    }, [currentUser?.aboutMeContentJson]);
    const handleAboutMeChange = (doc: TipTapDoc) => {
        editorTouchedRef.current = true;
        setAboutMeContentJson(doc);
    };

    const form = useForm({
        defaultValues: {
            firstName: currentUser?.firstName ?? "",
            lastName: currentUser?.lastName ?? "",
            organization: currentUser?.organizationName || undefined,
            email: currentUser?.email || "",
            bio: currentUser?.meta.bio || undefined,
            twoFactorEnabled: Boolean(currentUser?.twoFactorEnabled),
            interests: currentUser?.interests || undefined,
            calendarTimeZone: validateTimezone(currentUser?.calendarTimeZone),
        } as ProfileUpdate,
        validators: {
            onSubmit: ProfileUpdateSchema,
        },
        onSubmit: async ({ value }) => {
            setIsLoading(true);
            // updateProfile carries the plaintext fields; aboutMeContentJson
            // rides on the sibling updateAboutMe mutation so the recursive
            // TipTapDocSchema doesn't break react-form's inference here.
            await Promise.all([
                updateUserProfileMutate.mutateAsync(value),
                updateAboutMeMutate.mutateAsync({ aboutMeContentJson }),
            ]);
            await utils.users.me.invalidate();
            setIsLoading(false);
        },
    });

    const isDirty = useStore(form.store, (state) => state.isDirty);
    const firstName = useStore(form.store, (state) => state.values.firstName);
    const lastName = useStore(form.store, (state) => state.values.lastName);

    const updateUserProfileMutate = trpc.profiles.updateProfile.useMutation({
        onSuccess: () => {
            enqueueSnackbar("Profile updated successfully", { variant: "success" });
        },
        onError: (error) => {
            Sentry.captureException(error, {
                tags: { mutation: "profiles.updateProfile" },
                extra: { userId: currentUser?.id },
            });
            enqueueSnackbar(error.message, { variant: "error" });
        },
    });

    const updateAboutMeMutate = trpc.users.updateAboutMe.useMutation({
        onError: (error) => {
            Sentry.captureException(error, {
                tags: { mutation: "users.updateAboutMe" },
                extra: { userId: currentUser?.id },
            });
            enqueueSnackbar(`Failed to save Ask Me About: ${error.message}`, { variant: "error" });
        },
    });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
            }}
            className="col-span-2"
        >
            <div className="bg-brand-polar grid grid-cols-2 gap-6 rounded-2xl border border-[#c8c8c8] p-6 shadow-none">
                <div className="flex items-start space-x-3">
                    <IconWithTooltip
                        icon={<ProfileIcon className="h-5 w-5" />}
                        label="First Name"
                    />
                    <div className="flex-1">
                        <form.Field name="firstName">
                            {(field) => (
                                <>
                                    <Input
                                        id="firstName"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Enter your first name"
                                        className="w-full"
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip icon={<ProfileIcon className="h-5 w-5" />} label="Last Name" />
                    <div className="flex-1">
                        <form.Field name="lastName">
                            {(field) => (
                                <>
                                    <Input
                                        id="lastName"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Enter your last name"
                                        className="w-full"
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip icon={<Globe className="h-5 w-5" />} label="Timezone" />
                    <div className="flex-1">
                        <form.Field name="calendarTimeZone">
                            {(field) => (
                                <select
                                    id="calendarTimeZone"
                                    value={field.state.value ?? "US/Eastern"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    onBlur={field.handleBlur}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    {TIMEZONE_OPTIONS.map((tz) => (
                                        <option key={tz.value} value={tz.value}>
                                            {tz.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip
                        icon={<OrganizationIcon className="h-5 w-5" />}
                        label="Organization"
                    />
                    <div className="flex-1">
                        <form.Field name="organization">
                            {(field) => (
                                <>
                                    <Input
                                        id="organization"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder=""
                                        className="w-full"
                                        disabled
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip
                        icon={<EmailIcon className="h-5 w-5" />}
                        label="Email Address"
                    />
                    <div className="flex-1">
                        <form.Field name="email">
                            {(field) => (
                                <>
                                    <Input
                                        id="email"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Enter a secondary email"
                                        className="w-full"
                                        disabled
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip icon={<BioIcon className="h-5 w-5" />} label="Biography" />
                    <div className="flex-1">
                        <form.Field name="bio">
                            {(field) => (
                                <>
                                    <TextArea
                                        id="bio"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Tell us about yourself"
                                        className="w-full resize-y"
                                        rows={4}
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
                <div className="flex items-start space-x-3">
                    <IconWithTooltip icon={<Star className="h-5 w-5" />} label="Interests" />
                    <div className="flex-1">
                        <form.Field name="interests">
                            {(field) => (
                                <>
                                    <TextArea
                                        id="interests"
                                        name={field.name}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Tell us about your interests"
                                        className="w-full resize-y"
                                        rows={4}
                                    />
                                    <ValidationMessage
                                        error={field.state.meta.errors[0]?.message}
                                    />
                                </>
                            )}
                        </form.Field>
                    </div>
                </div>
            </div>
            {/* AskMeAbout component inserted here */}
            <div className="mt-6">
                <AskMeAbout
                    setEditorContent={handleAboutMeChange}
                    initialContent={aboutMeContentJson}
                />
            </div>
            {/* Save button */}
            <div className="bg-brand-polar col-span-2 mt-6 flex items-center justify-center rounded-2xl border border-[#c8c8c8] px-6 py-5 shadow-none">
                <Button
                    type="submit"
                    variant="primary"
                    disabled={!isDirty || isLoading || !firstName?.trim() || !lastName?.trim()}
                >
                    {isLoading ? (
                        <>
                            <div className="mr-2 flex h-3 w-3">
                                <div className="h-3 w-3 animate-ping rounded-full bg-gray-300"></div>
                            </div>
                            <span className="text-sm text-gray-100">Saving...</span>
                        </>
                    ) : (
                        <>
                            Save Profile <SubmitIcon className="ml-2" />
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
};
